/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { Image, ImageProps, Price } from '@adobe-commerce/elsie/components';
import { Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import {
  CartItemModel as NegotiableQuoteItemModel,
  QuoteDisplayAmount,
  StoreConfigModel,
} from '@/quote-management/data/models';
import { SwitchableAttributes } from '@/quote-management/containers/QuoteSummaryList/QuoteSummaryList';
import {
  transformBundleOptions,
  transformConfigurableOptions,
  transformCustomizableOptions,
} from './configurationTransformers';

const EAGER_LOAD_THRESHOLD = 4;

export interface ItemFormatterOptions {
  attributesToHide: SwitchableAttributes[];
  routeProduct?: (item: NegotiableQuoteItemModel) => string;
  slots?: {
    Thumbnail?: SlotProps<{
      item: NegotiableQuoteItemModel;
      defaultImageProps: ImageProps;
    }>;
    ProductAttributes?: SlotProps<{ item: NegotiableQuoteItemModel }>;
  };
  dictionary: Record<string, string>;
  quoteDisplaySettings?: StoreConfigModel['quoteDisplaySettings'];
}

/**
 * Get image component for an item
 */
export const getImage = (
  item: NegotiableQuoteItemModel,
  index: number,
  options: ItemFormatterOptions
) => {
  if (options.attributesToHide.includes('image')) {
    return undefined;
  }

  const defaultImageProps: ImageProps = {
    loading: index < EAGER_LOAD_THRESHOLD ? 'eager' : 'lazy',
    src: item.image.src,
    alt: item.image.alt,
    width: '300',
    height: '300',
    params: { width: 300 },
  };

  const imageComponent = (
    <Image
      data-testid="quote-summary-list-item-image"
      {...defaultImageProps}
    />
  );
  return (
    <Slot
      name="Thumbnail"
      slot={options.slots?.Thumbnail}
      context={{ item, defaultImageProps }}
    >
      {options.routeProduct ? (
        <a href={options.routeProduct(item)}>{imageComponent}</a>
      ) : (
        imageComponent
      )}
    </Slot>
  );
};

/**
 * Get title component for an item
 */
export const getTitle = (
  item: NegotiableQuoteItemModel,
  options: ItemFormatterOptions
) => {
  if (options.attributesToHide.includes('name')) {
    return undefined;
  }

  return (
    <span data-testid="quote-summary-list-item-title">
      {options.routeProduct ? (
        <a href={options.routeProduct(item)}>{item.product.name}</a>
      ) : (
        item.product.name
      )}
    </span>
  );
};

/**
 * Get SKU component for an item
 */
export const getSku = (item: NegotiableQuoteItemModel) => {
  return (
    <span data-testid="quote-summary-list-item-sku">{item.product.sku}</span>
  );
};

/**
 * Get product attributes slot for an item
 */
export const getProductAttributes = (
  item: NegotiableQuoteItemModel,
  options: ItemFormatterOptions
) => {
  return (
    <Slot
      name="ProductAttributes"
      slot={options.slots?.ProductAttributes}
      context={{ item }}
    />
  );
};

/**
 * Get configurations for an item (bundle, configurable, customizable options)
 */
export const getConfigurations = (
  item: NegotiableQuoteItemModel,
  options: ItemFormatterOptions
) => {
  if (options.attributesToHide.includes('configurations')) {
    return undefined;
  }

  const configuration = {
    ...transformBundleOptions(item.bundleOptions),
    ...transformConfigurableOptions(item.configurableOptions),
    ...transformCustomizableOptions(item.customizableOptions),
    ...(item.links && item.links.count
      ? item.links.count > 1
        ? {
            [options.dictionary.files.replace(
              '{count}',
              item.links.count.toString()
            )]: item.links.result,
          }
        : {
            [options.dictionary.file.replace(
              '{count}',
              item.links.count.toString()
            )]: item.links.result,
          }
      : null),
  };

  if (Object.keys(configuration).length === 0) {
    return undefined;
  }

  return configuration;
};

/**
 * Get total excluding tax component for an item
 */
export const getTotalExcludingTax = (
  item: NegotiableQuoteItemModel,
  options: ItemFormatterOptions
) => {
  return options.quoteDisplaySettings?.price ===
    QuoteDisplayAmount.TAX_INCLUDED_AND_EXCLUDED ? (
    <Price
      amount={item.prices.rowTotal.value}
      currency={item.prices.rowTotal.currency}
      data-testid="excluding-tax-total"
      aria-label={options.dictionary.regularPrice}
    />
  ) : undefined;
};

